import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Dialog } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../../api/provider.js";
import type { BalanceSubscriptionView } from "../../../api/slice/subscription.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
import { formatMoney } from "../../../money.js";
import { Block, type Outcome } from "../../account-admin/page/organization-page.js";
import { localisedOf, longDateText, namesText, talkTimeText, vatTextOf } from "./plan-copy.js";
import { RefusalLine } from "./plan-quote.js";

/**
 * "Your plan" — the running plan on `/finance/plans` (plan-020 **WAPP-PLAN**; design §32.6.7, §32.12.3 Q-21…Q-26,
 * §32.17): its name and state, the period, the included minutes used and left (from seconds, never rounded — OA-1),
 * the extra minutes and their cost as of the last day close, the next charge (net + VAT = total), the card with its
 * last four digits and its mandate state, and the acts: change (the chooser below), cancel at the period end (Q-21's
 * dialog, "Keep my plan"), withdraw a scheduled cancellation (Q-22, "Keep plan"). While a renewal is unpaid the plan
 * says so (Q-25 / Q-26) and links to the pay page (`/billing?pay={operation}`, WAPP-BILL's) and to Billing's card
 * replacement. Acts need `account.finance.manage` (Owner, Admin, Finance); reading needs `account.finance.read`.
 */

/** Billing's pay page for a renewal waiting for the person (design §32.10; WAPP-BILL builds the page). */
export function payPageOf(billingOperationId: string): string {
  return `/billing?pay=${encodeURIComponent(billingOperationId)}`;
}

/** The plan's name as the subscription read names it. */
export function heldNameOf(held: Pick<BalanceSubscriptionView, "displayName">): string {
  return held.displayName === null ? portalText("subscription", "card.custom_name") : localisedOf(held.displayName);
}

/** The state tag's words. */
export function heldStateText(state: string): string {
  switch (state) {
    case "past_due":
      return portalText("subscription", "held.state.past_due");
    case "suspended":
      return portalText("subscription", "held.state.suspended");
    case "pending_activation":
      return portalText("subscription", "held.state.pending_activation");
    default:
      return portalText("subscription", "held.state.active");
  }
}

/** Q-24: how the card is charged — by mandate, by a monthly link, or no longer at all. */
export function cardStateText(held: Pick<BalanceSubscriptionView, "card" | "collectionMode">): string | null {
  if (held.card === null) return null;
  if (held.card.mandateState === "revoked") return portalText("subscription", "held.card_dead");
  if (held.collectionMode === "mandate" && held.card.mandateState === "valid") return portalText("subscription", "held.card_mandate");
  return portalText("subscription", "held.card_link");
}

/** The cancellation dialog (Q-21), also opened from Billing → Plan (plan-020 WAPP-BILL). */
export function CancelDialog(props: { readonly open: boolean; readonly accountId: string; readonly held: BalanceSubscriptionView; readonly onClose: () => void; readonly onDone: (outcome: Outcome) => void }): ReactNode {
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const quote = useQuery({
    queryKey: ["subscription-quote", "cancellation", props.held.billingSubscriptionId] as const,
    queryFn: () => gateway.quoteSubscription(props.accountId, { kind: "subscription_cancellation", billingSubscriptionId: props.held.billingSubscriptionId }),
    enabled: props.open,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });
  const periodEnd = props.held.period === null ? null : longDateText(props.held.period.end, props.held.billingTimezone);
  const confirming = useMutation({
    mutationFn: async () => {
      const frozen = quote.data;
      if (frozen === undefined) throw new Error(portalText("finance", "m0688"));
      return gateway.confirmSubscriptionOperation(props.accountId, {
        billingQuoteId: frozen.billingQuoteId,
        idempotencyKey: `subscription-${frozen.billingQuoteId}`,
        termsRevision: null,
        consent: null,
        cardToken: null,
        kind: frozen.kind,
        amountDueMinor: frozen.amountDueMinor,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["balance-subscription"] });
      props.onClose();
      props.onDone({ tone: "done", text: () => portalText("subscription", "cancel.done", { period_end: periodEnd ?? "" }) });
    },
  });
  const over = quote.data?.detail.overLimitVoiceAgent ?? [];
  return (
    <Dialog
      open={props.open}
      title={portalText("subscription", "cancel.title", { plan: heldNameOf(props.held) })}
      onClose={props.onClose}
      actions={
        <>
          <Button variant="secondary" onClick={props.onClose}>{portalText("subscription", "cancel.keep")}</Button>
          <Button variant="primary" disabled={quote.data === undefined || confirming.isPending} onClick={() => confirming.mutate()}>
            {portalText("subscription", "cancel.confirm")}
          </Button>
        </>
      }
    >
      {periodEnd === null ? null : (
        <>
          <p className="c-note">{portalText("subscription", "cancel.stays", { period_end: periodEnd })}</p>
          <p className="c-note">{portalText("subscription", "cancel.returns", { period_end: periodEnd })}</p>
        </>
      )}
      {over.length > 0 && quote.data !== undefined && periodEnd !== null ? (
        <p className="c-note" role="status">
          {portalText("subscription", "quote.over_limit", { effective_date: periodEnd, after_limit: String(quote.data.detail.channelUnitAfter), voice_agents: namesText(over.map((one) => one.name)) })}
        </p>
      ) : null}
      {quote.isError ? <RefusalLine error={quote.error} otherwise={portalText("finance", "m0694")} onRequote={() => void quote.refetch()} /> : null}
      {confirming.isError ? <RefusalLine error={confirming.error} otherwise={portalText("finance", "m0694")} onRequote={() => void quote.refetch()} /> : null}
    </Dialog>
  );
}

export function HeldPlanBlock(props: {
  readonly accountId: string;
  readonly held: BalanceSubscriptionView;
  readonly canManage: boolean;
  readonly onChange: () => void;
  readonly onOutcome: (outcome: Outcome) => void;
}): ReactNode {
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const { held } = props;
  const [cancelOpen, setCancelOpen] = useState(false);
  const zone = held.billingTimezone;
  const periodEnd = held.period === null ? null : longDateText(held.period.end, zone);
  const withdrawing = useMutation({
    mutationFn: () => gateway.withdrawSubscriptionCancellation(held.billingSubscriptionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["balance-subscription"] });
      props.onOutcome({ tone: "done", text: () => portalText("subscription", "cancel.withdrawn") });
    },
  });
  const unpaid = held.state === "past_due" || held.state === "suspended";
  const cardState = cardStateText(held);
  const next = held.nextCharge;
  return (
    <Block title={portalText("subscription", "held.title")}>
      <div className="pl-held" aria-label={portalText("subscription", "held.title")}>
        <p className="pl-held-name">
          <span>{heldNameOf(held)}</span>
          <span className={unpaid ? "tag tag-accent-2 pl-option-tag" : "tag tag-accent pl-option-tag"}>{heldStateText(held.state)}</span>
          {held.cancelAtPeriodEnd && periodEnd !== null ? <span className="tag tag-neutral pl-option-tag">{portalText("subscription", "held.ends", { period_end: periodEnd })}</span> : null}
        </p>
        {held.state === "pending_activation" ? <p className="pl-line">{portalText("subscription", "held.pending")}</p> : null}
        {held.state === "past_due" && held.graceEndsAt !== null ? (
          <p className="pl-line pl-line-caution" role="status">{portalText("subscription", "held.past_due", { grace_end: longDateText(held.graceEndsAt, zone) })}</p>
        ) : null}
        {held.state === "suspended" && periodEnd !== null ? <p className="pl-line pl-line-caution" role="status">{portalText("subscription", "held.suspended", { period_end: periodEnd })}</p> : null}
        <dl className="pl-facts">
          {held.period === null ? null : (
            <div className="pl-fact">
              <dd>{portalText("subscription", "held.this_period", { start: longDateText(held.period.start, zone), end: longDateText(held.period.end, zone) })}</dd>
            </div>
          )}
          <div className="pl-fact">
            <dd>{portalText("subscription", "held.included", { used: talkTimeText(held.includedTalkSecondUsed), left: talkTimeText(held.includedTalkSecondLeft) })}</dd>
          </div>
          <div className="pl-fact">
            <dd>
              {portalText("subscription", "held.extra", { extra_minutes: talkTimeText(held.extraTalkSecond), extra_amount: formatMoney(held.extraAmount) })}
            </dd>
            <dd>{held.extraTimeEnabled ? portalText("subscription", "held.extra_time_on") : portalText("subscription", "held.extra_time_off")}</dd>
            <dd className="pl-fact-note">{portalText("subscription", "held.extra_as_of")}</dd>
          </div>
          {next === null ? null : (
            <div className="pl-fact">
              <dd>{portalText("subscription", "held.next_charge", { total: formatMoney(next.total), net: formatMoney(next.net), vat_text: vatTextOf(next.vat, null, null), date: longDateText(next.at, zone) })}</dd>
            </div>
          )}
          <div className="pl-fact">
            <dd>
              {held.card === null || held.card.lastFour === null ? portalText("subscription", "held.card_none") : portalText("subscription", "held.card", { last4: held.card.lastFour })}
              {cardState === null ? null : <span className="pl-fact-note"> · {cardState}</span>}
            </dd>
          </div>
        </dl>
      </div>
      {withdrawing.isError ? <RefusalLine error={withdrawing.error} otherwise={portalText("finance", "m0694")} /> : null}
      {props.canManage ? (
        <div className="pl-actions">
          {unpaid && held.payOperationId !== null ? (
            // The pay page is Billing's (`/billing?pay={operation}`, WAPP-BILL), the link every plan mail carries.
            <a href={payPageOf(held.payOperationId)} className="btn btn-primary">
              {portalText("subscription", "held.pay_now")}
            </a>
          ) : null}
          {unpaid || held.card === null || held.card.mandateState !== "valid" ? (
            <Link to="/billing" className="btn btn-secondary">
              {portalText("subscription", "held.replace_card")}
            </Link>
          ) : null}
          {held.cancelAtPeriodEnd ? (
            <Button variant="secondary" disabled={withdrawing.isPending} onClick={() => withdrawing.mutate()}>
              {portalText("subscription", "held.keep")}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={props.onChange}>{portalText("subscription", "held.change")}</Button>
              {held.state === "pending_activation" ? null : (
                <Button variant="ghost" onClick={() => setCancelOpen(true)}>{portalText("subscription", "held.cancel")}</Button>
              )}
            </>
          )}
        </div>
      ) : null}
      {props.canManage ? <CancelDialog open={cancelOpen} accountId={props.accountId} held={held} onClose={() => setCancelOpen(false)} onDone={props.onOutcome} /> : null}
    </Block>
  );
}
